// Cider Creek storefront analytics. Publishes cc_* custom events on Shopify's
// Customer Events bus (Shopify.analytics.publish); the "Cider Creek first-party
// events" custom pixel forwards them to Supabase (public.cc_web_events).
// Covers what Shopify's standard events can't see: the Vinoshipper cart and
// checkout, the age gate, signup forms, labelled clicks, scroll depth, section
// views, engaged time and 404s. Never sends anything a visitor types.
(() => {
  const pending = [];
  const publish = (name, data = {}) => {
    const analytics = window.Shopify?.analytics;
    if (analytics?.publish) analytics.publish(name, data);
    else pending.push([name, data]);
  };
  // Shopify's pixel manager can finish loading after this deferred script runs.
  const waitForPixels = setInterval(() => {
    const analytics = window.Shopify?.analytics;
    if (!analytics?.publish) return;
    clearInterval(waitForPixels);
    pending.splice(0).forEach(([name, data]) => analytics.publish(name, data));
  }, 250);
  setTimeout(() => clearInterval(waitForPixels), 15000);

  const template = document.querySelector('main[data-template]')?.dataset.template || '';
  const sectionOf = el => {
    const section = el.closest('.shopify-section');
    return section ? section.id.replace(/^shopify-section-(template|sections)--\d+__/, '') : '';
  };
  const labelOf = el => (el.getAttribute('aria-label') || el.innerText || el.value || el.title || '')
    .replace(/\s+/g, ' ').trim().slice(0, 80);

  // Clicks on anything interactive, with its visible label and where it sits.
  document.addEventListener('click', event => {
    const el = event.target.closest?.('a[href], button, [role="button"], summary, input[type="submit"], input[type="button"]');
    if (!el) return;
    let url = null;
    try {
      if (el.matches('a[href]')) url = new URL(el.getAttribute('href'), location.href);
    } catch {}
    const outbound = Boolean(url && /^https?:$/.test(url.protocol) && url.host !== location.host);
    publish('cc_click', {
      label: labelOf(el),
      tag: el.tagName.toLowerCase(),
      href: url ? (outbound ? url.href : url.pathname + url.search + url.hash) : '',
      outbound,
      section: sectionOf(el),
      template
    });
    if (el.matches('[data-cider-age-confirm]')) publish('cc_age_gate_confirmed', { template });
    else if (url && el.closest('[data-cider-age-gate]')) publish('cc_age_gate_declined', { template });
  }, true);

  // Form submissions: which form, never what was typed.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const formType = form.querySelector('input[name="form_type"]')?.value || '';
    const details = { form: form.id || form.classList[0] || formType, section: sectionOf(form), template };
    if (formType === 'customer') {
      publish('cc_signup', { ...details, tags: form.querySelector('input[name="contact[tags]"]')?.value || '' });
    } else if (formType === 'contact') {
      publish('cc_contact_submit', details);
    }
  }, true);
  const params = new URLSearchParams(location.search);
  if (params.get('customer_posted') === 'true') publish('cc_signup_success', { template });
  if (params.get('contact_posted') === 'true') publish('cc_contact_success', { template });

  if (template === '404') publish('cc_404', { path: location.pathname, referrer: document.referrer });

  // Scroll depth milestones. Desktop (990px+) scrolls .page-wrapper, mobile
  // scrolls the window; element scrolls don't bubble, so listen in capture.
  const marks = [25, 50, 75, 90, 100];
  let maxScroll = 0;
  let scrollQueued = false;
  const scroller = () => {
    const wrapper = document.querySelector('.page-wrapper');
    return wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : document.scrollingElement;
  };
  const measureScroll = () => {
    scrollQueued = false;
    const el = scroller();
    const scrollable = el.scrollHeight - el.clientHeight;
    if (scrollable <= 0) return;
    const percent = Math.min(100, Math.round((el.scrollTop / scrollable) * 100));
    maxScroll = Math.max(maxScroll, percent);
    while (marks.length && percent >= marks[0]) publish('cc_scroll_depth', { percent: marks.shift(), template });
  };
  document.addEventListener('scroll', () => {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(measureScroll);
  }, { passive: true, capture: true });

  // Which sections people actually see (half the section, or half the screen).
  const watchSections = () => {
    if (!('IntersectionObserver' in window)) return;
    const sections = [...document.querySelectorAll('main .shopify-section')];
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const seen = entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight * 0.5;
        if (!entry.isIntersecting || !seen) return;
        observer.unobserve(entry.target);
        publish('cc_section_view', { section: sectionOf(entry.target), index: sections.indexOf(entry.target), template });
      });
    }, { threshold: [0, 0.25, 0.5] });
    sections.forEach(section => observer.observe(section));
  };

  // Age gate: shown → confirmed or declined. Sections only count once it's cleared.
  const gate = document.querySelector('[data-cider-age-gate]');
  if (gate && !gate.hidden) {
    publish('cc_age_gate_shown', { template });
    gate.querySelector('[data-cider-age-confirm]')?.addEventListener('click', watchSections, { once: true });
  } else {
    watchSections();
  }

  // Engaged time: visible tab with input in the last 30 seconds.
  let engagedMs = 0;
  let reportedMs = 0;
  let lastActive = Date.now();
  let lastTick = Date.now();
  ['pointerdown', 'keydown', 'scroll', 'mousemove', 'touchstart'].forEach(type => {
    document.addEventListener(type, () => { lastActive = Date.now(); }, { passive: true, capture: true });
  });
  setInterval(() => {
    const now = Date.now();
    if (document.visibilityState === 'visible' && now - lastActive < 30000) engagedMs += now - lastTick;
    lastTick = now;
  }, 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden' || engagedMs - reportedMs < 1000) return;
    publish('cc_engagement', {
      engaged_seconds: Math.round((engagedMs - reportedMs) / 1000),
      total_seconds: Math.round(engagedMs / 1000),
      max_scroll: maxScroll,
      template
    });
    reportedMs = engagedMs;
  });

  // Vinoshipper cart and checkout. The injector dispatches these on window.
  const pageHandle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '';
  const vsHandles = new Map();
  document.querySelectorAll('.ccpdp__vs-buy[data-vs-product-id]').forEach(el => vsHandles.set(el.dataset.vsProductId, pageHandle));
  document.querySelectorAll('[data-vs-pool-for][data-vs-product-id]').forEach(el => vsHandles.set(el.dataset.vsProductId, el.dataset.vsPoolFor));
  const vsItem = (value = {}) => {
    const id = String(value.id ?? '');
    return {
      vs_product_id: id,
      product_handle: vsHandles.get(id) || '',
      product_name: value.product?.name || value.name || '',
      price: Number(value.product?.price ?? value.price) || null,
      quantity: Number(value.qty) || 1
    };
  };
  const vsCart = () => {
    const items = (window.Vinoshipper?.getCart?.()?.contents || []).map(vsItem);
    return {
      items,
      item_count: items.reduce((count, item) => count + item.quantity, 0),
      value: Math.round(items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0) * 100) / 100,
      template
    };
  };
  window.addEventListener('vinoshipper:product:add', event => publish('cc_vs_add_to_cart', { ...vsItem(event.detail?.value), template }));
  window.addEventListener('vinoshipper:cart:remove', event => publish('cc_vs_remove_from_cart', { ...vsItem(event.detail?.value), template }));
  window.addEventListener('vinoshipper:cart:opened', () => publish('cc_vs_cart_opened', vsCart()));
  window.addEventListener('vinoshipper:cart:checkout', () => publish('cc_vs_checkout', vsCart()));
  window.addEventListener('vinoshipper:page:view', event => publish('cc_vs_page_view', { page_name: event.detail?.pageName || '', template }));
})();
